import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { BACKEND_API_URL } from './backend-api.tokens';
import { SpaqFireFunctions } from './spaq-fire-functions.service';

/**
 * Thrown by `BackendApiService`'s exclusive calls (e.g. `createBookingDay`/`deleteBookingDay`)
 * when a call for the same key is already in flight and this one was discarded as a result.
 * Callers that want to ignore/handle this case distinctly from a real backend failure can
 * `catch (e) { if (e instanceof CallInFlightError) { ... } }`.
 */
export class CallInFlightError extends Error {
	constructor(public readonly key: string) {
		super(`A call for "${key}" is already in flight; this call was discarded.`);
		this.name = 'CallInFlightError';
	}
}

/**
 * Single entry point for every backend call made by the SPAQ apps. Cloud Functions are
 * `onRequest` endpoints posted to `BACKEND_API_URL`. `SpaqFireFunctions.httpsCallable` remains
 * only for CEM callables that have no function in this backend.
 *
 * Shared by `spaq-abonnement-frontend` (public) and `spaq-abonnement-cem`. It is the union of
 * what both apps previously kept in their own `core/services/backend-api.service.ts`:
 *
 *  - The `V3` surface and `contactUs` / `monerisVault` / `queueitValidate` / `serviceCreate`
 *    are used by both apps.
 *  - `queueitCancel` currently has a public-only call site.
 *  - `getParksContractsAcount` / `fetchServicePrice` / `showLogs` / `showUpdateUserLogs` and the
 *    `/spaq/*` Express surface are CEM-only. They are inert in public (never injected there) and
 *    kept here so the two apps share one class — see `docs/BACKEND_CALLS_INVENTORY.md`.
 *
 * `BACKEND_API_URL` is provided by each app from its `environment.backendApiUrl`.
 */
@Injectable({ providedIn: 'root' })
export class BackendApiService {
	constructor(
		private fns: SpaqFireFunctions,
		private http: HttpClient,
		@Inject(BACKEND_API_URL) private backendApiUrl: string
	) {}

	/**
	 * Runs `call` under `key` only if no call under that key is currently in flight; while one is,
	 * every other call for that key is discarded outright (never queued) and errors with
	 * `CallInFlightError` instead of hitting the backend. Once the in-flight call finishes, the key
	 * is freed and the next call made for it runs for real. Use for calls that mutate shared state
	 * and must never race each other — e.g. `createBookingDay`/`deleteBookingDay`, which both
	 * mutate the same booking's day list.
	 */
	private readonly busyKeys = new Set<string>();

	private runExclusive<T>(key: string, call: () => Observable<T>): Observable<T> {
		if (this.busyKeys.has(key)) return throwError(() => new CallInFlightError(key));
		this.busyKeys.add(key);
		return call().pipe(finalize(() => this.busyKeys.delete(key)));
	}

	/** POST a Cloud Function. `ScopeInterceptor` adds the bearer token and `X-Spaq-Scope`. */
	private post(name: string, payload: unknown = {}): Observable<any> {
		return this.http.post(`${this.backendApiUrl}/${name}`, payload ?? {}).pipe(
			catchError(error => {
				if (!(error instanceof HttpErrorResponse)) return throwError(() => error);
				const body = error.error;
				const message = body && typeof body === 'object' && typeof body.error === 'string' ? body.error : error.message;
				const code = body && typeof body === 'object' && typeof body.code === 'string' ? body.code : undefined;
				const wrapped = new Error(message) as Error & { status: number; code?: string; error: unknown };
				wrapped.status = error.status;
				wrapped.code = code;
				wrapped.error = body;
				return throwError(() => wrapped);
			})
		);
	}

	// ─────────────────────────────────────────────────────────────────────────
	// 1. Cloud Functions (onRequest)
	// ─────────────────────────────────────────────────────────────────────────

	/** `web/admin/user/user-list.component.ts`, `web/admin/mail/mail-list.component.ts`,
	 *  `web/admin/access-code/access-code-search.component.ts`, `web/admin/contract/contract-list.component.ts` */
	adminDataSearch(payload: any): Observable<any> {
		return this.post('adminDataSearchV3', { ...payload, method: 'POST' });
	}

	/** `web/admin/admin.service.ts` */
	adminDeleteUser(payload: any): Observable<any> {
		return this.post('adminDeleteUserV3', payload);
	}

	/** `web/admin/admin.service.ts`. The handler ignores the body, but Cloud Functions
	 *  rejects a JSON boolean (`true`) with 400 before the function runs. */
	adminListUser(): Observable<any> {
		return this.post('adminListUserV3', {});
	}

	/** `web/admin/admin.service.ts` */
	adminUpdateUser(payload: any): Observable<any> {
		return this.post('adminUpdateUserV3', payload);
	}

	/** `core/effects/ppa.ts` */
	addPpa(payload: any): Observable<any> {
		return this.post('addPpaV3', payload);
	}

	/** `core/effects/subscription-edit.ts` (`upload$`) */
	attachmentsCreate(payload: any): Observable<any> {
		return this.post('attachmentsCreateV3', payload);
	}

	/** `modules/booking/pages/booking-page.component.ts` */
	bookingAvailability(payload: any): Observable<any> {
		return this.post('bookingAvailabilityV3', payload);
	}

	/** `modules/booking/pages/booking-page.component.ts` */
	bookingService(payload: any): Observable<any> {
		return this.post('bookingServiceV3', payload);
	}

	/** `modules/contact/pages/contact.component.ts` */
	callTransferService(payload: any): Observable<any> {
		return this.post('callTransferServiceV3', payload);
	}

	/** `core/effects/contract.ts` (`cancelContract$`) */
	cancelContract(payload: any): Observable<any> {
		return this.post('cancelContractV3', payload);
	}

	/** `core/effects/contract.ts` (`cancelContractService$`) */
	cancelContractService(payload: any): Observable<any> {
		return this.post('cancelContractServiceV3', payload);
	}

	/** `modules/booking/pages/booking-page.component.ts` */
	createBooking(payload: any): Observable<any> {
		return this.post('createBookingV3', payload);
	}

	/** `modules/booking/pages/booking-page.component.ts`.
	 *  Both apps attach a `queueit` block via `QueueItService.getRequest()`. Scope comes from
	 *  `X-Spaq-Scope` (ScopeInterceptor). The backend decides whether the queue is active via
	 *  `assertQueueItPass`; if it is, this block must be present and valid, and the pass is
	 *  cancelled after a successful confirm. */
	createBookingConfirm(payload: any): Observable<any> {
		return this.http.post(`${this.backendApiUrl}/createBookingConfirmV3`, payload);
	}

	/** `modules/booking/pages/booking-page.component.ts`. Exclusive — see `runExclusive`. */
	createBookingDay(payload: any): Observable<any> {
		return this.runExclusive('bookingDay', () => this.post('createBookingDayV3', payload));
	}

	/** `modules/booking/pages/booking-page.component.ts` */
	createBookingTransfer(payload: any): Observable<any> {
		return this.post('createBookingTransferV3', payload);
	}

	/** `modules/contract/pages/contract-details.component.ts` */
	createMobileCard(payload: any): Observable<any> {
		return this.post('createMobileCardV3', payload);
	}

	/** `core/services/auth.service.ts` (registration + email/password sign-up) */
	createUser(payload: any): Observable<any> {
		return this.post('createUserV3', payload);
	}

	/** `modules/booking/pages/booking-page.component.ts`. Exclusive — see `runExclusive`. */
	deleteBookingDay(payload: any): Observable<any> {
		return this.runExclusive('bookingDay', () => this.post('deleteBookingDayV3', payload));
	}

	/** `core/effects/layout.ts` */
	deleteMyAccount(payload: any = {}): Observable<any> {
		return this.post('deleteMyAccountV3', payload);
	}

	/** `modules/booking/pages/booking-page.component.ts` */
	fetchBooking(payload: any): Observable<any> {
		return this.post('fetchBookingV3', payload);
	}

	/** `modules/booking/pages/booking-page.component.ts` */
	fetchBookingHistory(payload: any): Observable<any> {
		return this.post('fetchBookingHistoryV3', payload);
	}

	/** `modules/contract/pages/contract-select-service.component.ts`, `contract-pay.component.ts` */
	fetchServicesCapacity(payload: any): Observable<any> {
		return this.post('fetchServicesCapacityV3', payload);
	}

	/** `web/admin/user/user-list.component.ts` (`signInAsUser`) */
	getUserToken(payload: any): Observable<any> {
		return this.post('getUserTokenV3', payload);
	}

	/** `modules/payment/components/moneris-pay.component.ts` */
	handleMonerisPayment(payload: any): Observable<any> {
		return this.post('handleMonerisPaymentV3', payload);
	}

	/** `modules/payment/components/moneris-pay.component.ts` */
	monerisGetTicketNumber(payload: any): Observable<any> {
		return this.post('monerisGetTicketNumberV3', payload);
	}

	/** `core/effects/contract.ts` (`payment$`) */
	paymentCreate(payload: any): Observable<any> {
		return this.post('paymentCreateV3', payload);
	}

	/** `core/services/auth.service.ts` (`checkCode`, session refresh) */
	refreshUser(payload: any): Observable<any> {
		return this.post('refreshUserV3', payload);
	}

	/** `core/effects/contract.ts`, `core/effects/register-subscription.ts` */
	subscriptionCreate(payload: any): Observable<any> {
		return this.post('subscriptionCreateV3', payload);
	}

	/** `core/effects/contract.ts` (`search$`) */
	subscriptionSearch(payload: any): Observable<any> {
		return this.post('subscriptionSearchV3', payload);
	}

	/** `core/effects/subscription-edit.ts` (`update$`) */
	subscriptionUpdate(payload: any): Observable<any> {
		return this.post('subscriptionUpdateV3', payload);
	}

	/** `core/effects/auth.ts`, `core/effects/user.ts`, `modules/contract/pages/contract-details.component.ts`
	 *  (both apps). Always called with no payload — `syncAccountV3` derives the account from the caller's auth. */
	syncAccount(): Observable<any> {
		return this.post('syncAccountV3', {});
	}

	/** `web/admin/admin.service.ts` (`syncUser`) */
	syncUserAccount(payload: any): Observable<any> {
		return this.post('syncUserAccountV3', payload);
	}

	/** `web/admin/profile/profile.component.ts` (`onClickSyncAllParks`). Payload is `{ park, sector }`. */
	syncTgpsByParkAndSector(payload: { park: string | number; sector: string | number }): Observable<any> {
		return this.post('syncTgpsByParkAndSectorV3', payload);
	}

	/** `web/admin/profile/profile.component.ts` (`onClickUpdateParks`) */
	syncTgpsParks(payload: any = {}): Observable<any> {
		return this.post('syncTgpsParksV3', payload);
	}

	/** `web/admin/admin.service.ts` (`updateUserContractNo`) */
	updateUserContractNo(payload: { userId: any; contractNo: any }): Observable<any> {
		return this.post('updateUserContractNoV3', payload);
	}

	/** `modules/csdm/pages/csdm-view-page.component.ts` */
	uploadCsdm(payload: any): Observable<any> {
		return this.post('uploadCsdmV3', payload);
	}

	/** `core/services/vehicles.service.ts` (`create`) */
	vehicleCreate(payload: any): Observable<any> {
		return this.post('vehicleCreateV3', payload);
	}

	/** `core/effects/user-vehicles.ts` */
	vehicleDelete(payload: any): Observable<any> {
		return this.post('vehicleDeleteV3', payload);
	}

	/** `core/services/vehicles.service.ts` (`update`) */
	vehicleUpdate(payload: any): Observable<any> {
		return this.post('vehicleUpdateV3', payload);
	}

	// --- CEM-only callables (no public call site — see docs/BACKEND_CALLS_INVENTORY.md §1) ---

	/** `web/admin/admin.service.ts` (`getParksContractsCount`). No `V3` suffix, and "Acount" is a typo
	 *  in the deployed function name — reproduced verbatim, do not "fix" without updating the backend too. */
	getParksContractsAcount(payload: any = true): Observable<any> {
		return this.fns.httpsCallable('getParksContractsAcount')(payload);
	}

	/** `modules/services/components/service-detail.component.ts`. No `V3` suffix. */
	fetchServicePrice(payload: any): Observable<any> {
		return this.fns.httpsCallable('fetchServicePrice')(payload);
	}

	/** `web/admin/logs/logs-viewer.component.ts`. No `V3` suffix. */
	showLogs(payload: any): Observable<any> {
		return this.fns.httpsCallable('showLogs')(payload);
	}

	/** `web/admin/user/user-list.component.ts` (`updateLogs`) */
	showUpdateUserLogs(payload: { uid: any }): Observable<any> {
		return this.fns.httpsCallable('showUpdateUserLogsV3')(payload);
	}

	// ─────────────────────────────────────────────────────────────────────────
	// 2. Raw HttpClient calls (BACKEND_API_URL + Express `onRequest` endpoints)
	// ─────────────────────────────────────────────────────────────────────────

	/** `core/effects/contact.ts` */
	contactUs(payload: any): Observable<any> {
		return this.http.post(`${this.backendApiUrl}/contactUsV3`, payload);
	}

	/** `modules/payment/components/moneris-vault.component.ts`. Response is plain text, not JSON. */
	monerisVault(payload: any): Observable<string> {
		return this.http.post(`${this.backendApiUrl}/monerisVaultV3`, payload, { responseType: 'text' });
	}

	/** Used by `QueueItService.check`. `Authorization` + `X-Spaq-Scope` are stamped by each app's `ScopeInterceptor`. */
	queueitValidate(payload: {
		eventType: string;
		targetUrl: string;
		queueitToken: string;
	}): Observable<{ queueItRequired: boolean; redirectUrl?: string }> {
		return this.http.post<{ queueItRequired: boolean; redirectUrl?: string }>(`${this.backendApiUrl}/queueitValidateV3`, payload);
	}

	/** `core/effects/contract.ts` (`addSubscriptionService$`) in both apps.
	 *  Both apps attach a `queueit` block via `QueueItService.getRequest()`. The backend
	 *  decides whether the queue is active via `assertQueueItPass`; if it is, this
	 *  block must be present and valid.
	 *  `Authorization` + `X-Spaq-Scope` are stamped by each app's `ScopeInterceptor`. */
	serviceCreate(payload: any): Observable<any> {
		return this.http.post(`${this.backendApiUrl}/serviceCreateV3`, payload);
	}

	// --- CEM-only HTTP calls: the `/spaq/*` Express surface (no public call site — public's
	// vehicles/services read Firebase Realtime Database directly instead; see
	// docs/BACKEND_CALLS_INVENTORY.md §3.5) ---

	/** `core/services/auth.service.ts` (`checkPhone`) */
	checkPhone(phoneNumber: string): Observable<any> {
		return this.http.get(`${this.backendApiUrl}/spaq/check_phone/+1${phoneNumber}`);
	}

	/** `core/services/payment.service.ts` (`addPPA`) */
	spaqBankInformation(payload: any): Observable<any> {
		return this.http.post(`${this.backendApiUrl}/spaq/bankInformation`, payload);
	}

	/** `core/services/services.service.ts` (`getServicesByContractNo`) */
	spaqServices(): Observable<any> {
		return this.http.get(`${this.backendApiUrl}/spaq/services`);
	}

	/** `core/services/spaq-api.service.ts` (`getServicesByContractNo`) */
	spaqSubscriptionServices(contractNo: string | number): Observable<any[]> {
		return this.http.get<any[]>(`${this.backendApiUrl}/spaq/subscription/${contractNo}/services`);
	}

	/** `core/services/spaq-api.service.ts` (`getSubscriptionByContractNo`) */
	spaqSubscription(contractNo: string | number, payload: { phone: any; postalCode: any }): Observable<any> {
		return this.http.post(`${this.backendApiUrl}/spaq/subscription/${contractNo}`, payload);
	}

	/** `core/services/spaq-api.service.ts` (`getVehiclesByContractNo`) */
	spaqSubscriptionVehicles(contractNo: string | number): Observable<any> {
		return this.http.get(`${this.backendApiUrl}/spaq/subscription/${contractNo}/vehicles`);
	}

	/** `core/services/vehicles.service.ts` (`addToUser`) */
	spaqVehiclesCreate(payload: any): Observable<any[]> {
		return this.http.post<any[]>(`${this.backendApiUrl}/spaq/vehicles`, payload);
	}

	/** `core/services/vehicles.service.ts` (`getFromUser`) */
	spaqVehiclesList(): Observable<any> {
		return this.http.get(`${this.backendApiUrl}/spaq/vehicles`);
	}

	/** `core/services/vehicles.service.ts` (`removeToUser`) */
	spaqVehicleDelete(id: string | number): Observable<any[]> {
		return this.http.delete<any[]>(`${this.backendApiUrl}/spaq/vehicles/${id}`);
	}

	/** `core/services/vehicles.service.ts` (`updateToUser`) */
	spaqVehicleUpdate(id: string | number, payload: any): Observable<any[]> {
		return this.http.put<any[]>(`${this.backendApiUrl}/spaq/vehicles/${id}`, payload);
	}

	// NOTE: `modules/contract/pages/contract-pay.component.ts` (`simulateMoneris`) used to POST to
	// `/monerisVault` and `/monerisPayment` (no `V3` suffix) with hardcoded fake success payloads —
	// dead/mock test scaffolding. Removed at the call site; deliberately not carried here. See
	// `docs/BACKEND_CALLS_INVENTORY.md` §3.3.
}
